import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Chip,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import { solicitudesService } from '../services';
import { authenticationService } from '../services';
import './Solicitudes.css';

const Solicitudes = () => {
  const history = useHistory();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para filtros
  const [busquedaTitular, setBusquedaTitular] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');

  const cargarSolicitudes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await solicitudesService.obtenerSolicitudes();
      setSolicitudes(data);
    } catch (err) {
      setError(err.message);
      if (err.message.includes('Sesión expirada')) {
        setTimeout(() => {
          history.push('/login');
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  }, [history]);

  useEffect(() => {
    // Verificar sesión antes de cargar
    if (!authenticationService.checkSessionValidity()) {
      history.push('/login');
      return;
    }

    cargarSolicitudes();
  }, [history, cargarSolicitudes]);

  // Obtener estados únicos para el filtro
  const estadosUnicos = useMemo(() => {
    const estados = [...new Set(solicitudes.map(s => s.estado).filter(Boolean))];
    return estados.sort();
  }, [solicitudes]);

  // Filtrar y ordenar solicitudes
  const solicitudesFiltradas = useMemo(() => {
    let resultado = [...solicitudes];
    
    // Filtrar por titular
    if (busquedaTitular.trim()) {
      const busqueda = busquedaTitular.toLowerCase().trim();
      resultado = resultado.filter(s => 
        s.titular?.toLowerCase().includes(busqueda)
      );
    }
    
    // Filtrar por estado
    if (filtroEstado) {
      resultado = resultado.filter(s => s.estado === filtroEstado);
    }
    
    // Ordenar por última modificación (más reciente primero)
    resultado.sort((a, b) => {
      const fechaA = a.fechaUltimaModificacion ? new Date(a.fechaUltimaModificacion) : new Date(0);
      const fechaB = b.fechaUltimaModificacion ? new Date(b.fechaUltimaModificacion) : new Date(0);
      return fechaB - fechaA;
    });
    
    return resultado;
  }, [solicitudes, busquedaTitular, filtroEstado]);

  const formatearFecha = (fechaString) => {
    if (!fechaString) return '-';
    try {
      const fecha = new Date(fechaString);
      return fecha.toLocaleDateString('es-AR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return fechaString;
    }
  };

  const getEstadoStyle = (estado) => {
    const estilos = {
      'ERROR_DETECTADO': { backgroundColor: '#f44336', color: '#fff' }, // Rojo
      'FIRMADO': { backgroundColor: '#81c784', color: '#fff' }, // Verde claro
      'INCOMPLETA': { backgroundColor: '#9c27b0', color: '#fff' }, // Violeta
      'APROBADA': { backgroundColor: '#2e7d32', color: '#fff' }, // Verde oscuro
      'PENDIENTE_FIRMA': { backgroundColor: '#03a9f4', color: '#fff' }, // Celeste
      'PENTIENTE_FIRMA': { backgroundColor: '#03a9f4', color: '#fff' }, // Celeste (typo)
      'RECHAZADA': { backgroundColor: '#f44336', color: '#fff' }, // Rojo
    };
    return estilos[estado] || { backgroundColor: '#9e9e9e', color: '#fff' }; // Gris por defecto
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      'INDIVIDUO': 'Individuo',
      'EMPRESA_CLASICA': 'Empresa Clásica',
      'EMPRESA_SGR': 'Empresa SGR'
    };
    return tipos[tipo] || tipo;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        p={3} 
        style={{
          backgroundColor: '#ffebee',
          border: '1px solid #f44336',
          borderRadius: '4px',
          color: '#c62828'
        }}
      >
        <Typography variant="body1" style={{ fontWeight: 500 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom style={{ marginBottom: '1.5rem' }}>
        Solicitudes de Apertura
      </Typography>

      {/* Filtros */}
      <Paper style={{ padding: '1rem', marginBottom: '1.5rem' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Buscar por titular..."
              value={busquedaTitular}
              onChange={(e) => setBusquedaTitular(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                label="Estado"
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {estadosUnicos.map((estado) => (
                  <MenuItem key={estado} value={estado}>
                    {estado}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={5}>
            <Typography variant="body2" color="textSecondary">
              Mostrando {solicitudesFiltradas.length} de {solicitudes.length} solicitudes
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Titular</strong></TableCell>
              <TableCell className="hide-mobile"><strong>Tipo</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell className="hide-mobile"><strong>Fecha Alta</strong></TableCell>
              <TableCell className="hide-mobile"><strong>Última Modificación</strong></TableCell>
              <TableCell className="hide-mobile"><strong>Fecha Aprobación</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {solicitudesFiltradas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary" style={{ padding: '2rem' }}>
                    {solicitudes.length === 0 
                      ? 'No hay solicitudes disponibles' 
                      : 'No se encontraron solicitudes con los filtros aplicados'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              solicitudesFiltradas.map((solicitud) => (
                <TableRow 
                  key={solicitud.id} 
                  hover
                  style={{ cursor: 'pointer' }}
                  onClick={() => history.push(`/solicitudes/${solicitud.id}`, { solicitud })}
                >
                  <TableCell>{solicitud.id}</TableCell>
                  <TableCell>{solicitud.titular || '-'}</TableCell>
                  <TableCell className="hide-mobile">{getTipoLabel(solicitud.tipo)}</TableCell>
                  <TableCell>
                    <Chip
                      label={solicitud.estado}
                      size="small"
                      style={getEstadoStyle(solicitud.estado)}
                    />
                  </TableCell>
                  <TableCell className="hide-mobile">{formatearFecha(solicitud.fechaAlta)}</TableCell>
                  <TableCell className="hide-mobile">{formatearFecha(solicitud.fechaUltimaModificacion)}</TableCell>
                  <TableCell className="hide-mobile">{formatearFecha(solicitud.fechaAprobo)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Solicitudes;
